import React from 'react';
import {ReactComponent as CarIco} from "../../../../../images/icons/car.svg";
import RoundedIcon from "../../../Common/RoundedIcon";
import {useTranslation, withTranslation} from "react-i18next";
import {getSession, selectLocale} from "../../../../services/Session/reducer";
import {connect, DispatchProp} from "react-redux";
import {RootState} from "../../../../../reducers";
import {getCart} from "../../../../services/Cart/reducer";
import HoursHelper from "../../../../services/Menu/services/Availability/HoursHelper";
import DateService from "../../../../services/Common/Date/DateService";
import {getStoreConfig} from "../../../../services/Store/reducer";
import Money from "../../../Common/Money";
import {ReactComponent as DropdownIco} from "../../../../../images/icons/arrow-bottom.svg";
import {anyMenuHasPickDate} from "../../utils";
import AvailableHelper from "../../../../services/Menu/services/Availability/AvailableHelper";
import {OrderType} from "../../../../services/Api/types";
import {DateHelper} from "../../../../services/Common/Date/DateHelper";
import {getAvailabilities, getMenus} from "../../../../services/Menu/reducer";
import StoreUtils from "../../../../services/Store/StoreUtils";

interface OwnProps {
    handleShowOrderDetailsModal: () => void;
    handleOnKeyDown: (event: (React.KeyboardEvent<HTMLDivElement> | KeyboardEvent)) => Promise<void>;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps & DispatchProp;

const CartButtonDeliveryInfo = ({
                                    session,
                                    config,
                                    cart,
                                    locale,
                                    handleShowOrderDetailsModal,
                                    availabilities,
                                    handleOnKeyDown,
                                    menus
                                }: Props) => {
    const {t} = useTranslation();
    const isStoreOpen = HoursHelper.isOpen(config?.hours, DateService.now());
    const orderTypeMenuAvailabilities = AvailableHelper
        .getAvailabilities(availabilities, menus
            .map((menu: any) => menu[OrderType.DELIVERY.toLowerCase()] && menu.availability_id));

    const getInfoDetails = (): {
        topInfo: string | JSX.Element;
        bottomInfo: string | JSX.Element
    } => {
        const pickDateAvailable = anyMenuHasPickDate(menus, OrderType.DELIVERY);

        if (config && !StoreUtils.getOrderTypes(config).includes(OrderType.DELIVERY)) {
            return {
                topInfo: (
                    <span className="font-weight-bold text-danger">
                        {t("common.word.not_available").toLowerCase()}
                    </span>
                ),
                bottomInfo: <span className="text-primary">
                     {t("modules.cart.field.change_order_type.title")}
                    </span>
            }
        }

        if (!session.pickup_at) {
            if (isStoreOpen) {
                const isOpenForCurrentType = HoursHelper.isOpen(orderTypeMenuAvailabilities.flatMap((availability: any) => availability.hours), DateService.now())
                    && orderTypeMenuAvailabilities.some((availability: any) => AvailableHelper.isAvailabile(availability, DateService.now()));
                const deliveryAvailable = isDeliveryAvailable();

                if (!isOpenForCurrentType) {
                    const nearestOpenHours = AvailableHelper.getNearestOpenDateFromNowOrDate(orderTypeMenuAvailabilities)?.dateFrom;
                    const date = nearestOpenHours ? DateHelper.getFormattedNearestDate(DateHelper.format(nearestOpenHours), t, locale ?? "") : undefined;
                    const pickDateAvailable = anyMenuHasPickDate(menus, OrderType.DELIVERY);

                    let bottomInfo: JSX.Element = <></>;

                    if (pickDateAvailable) {
                        bottomInfo = <span
                            className="text-primary">{`${t("modules.cart.field.change_date_or_order_type.title")}.`}</span>
                    } else {
                        bottomInfo = date ? <span
                            className="text-primary">{t("modules.cart.field.change_order_type.title", {date})}</span> : <></>
                    }

                    return {
                        topInfo: <span className="text-danger">{t("modules.cart.field.not_available_now.title")}</span>,
                        bottomInfo
                    }
                }

                if (!deliveryAvailable) {
                    return {
                        topInfo: (
                            <span className="font-weight-bold text-danger">
                                {t("modules.cart.field.delivery_unavailable.title")}
                            </span>
                        ),
                        bottomInfo: drawDeliveryInfo(),
                    }
                }

                return {
                    topInfo: (
                        <span className='text-success'>
                                {t("common.word.as_soon_as_possible").toLowerCase()}
                            </span>
                    ),
                    bottomInfo: drawDeliveryInfo(),
                }
            } else {
                const nearestOpenHours = AvailableHelper.getNearestOpenDateFromNowOrDate(orderTypeMenuAvailabilities)?.dateFrom;
                const date = nearestOpenHours ? DateHelper.getFormattedNearestDate(DateHelper.format(nearestOpenHours), t, locale ?? "") : undefined;
                const topInfo = <span className="text-danger">{t("modules.cart.field.closed_now.title")}</span>;

                if (pickDateAvailable) {
                    return {
                        topInfo,
                        bottomInfo: <span
                            className="text-primary">{`${t("modules.cart.field.change_date_to_order.title")}.`}</span>
                    }
                }

                return {
                    topInfo,
                    bottomInfo: <span className="text-muted">{t("modules.cart.field.open_at.title", {date})}</span>
                }
            }
        } else {
            if (!pickDateAvailable && session.pickup_at) {
                return {
                    topInfo: (
                        <span className="font-weight-bold text-danger">
                                {t("modules.cart.field.pickup_at_unavailable.title")}
                            </span>
                    ),
                    bottomInfo: <span className="text-primary">
                     {t("modules.cart.field.change_date.title")}
                    </span>
                }
            }

            return {
                topInfo: (
                    <span className='text-success'>
                        {DateHelper.getFormattedNearestDate(session.pickup_at, t, locale ?? "")}
                    </span>
                ),
                bottomInfo: drawDeliveryInfo(),
            };
        }
    }

    const isDeliveryAvailable = () => {
        if (session.full_address === "") {
            return true;
        }

        return session.in_delivery_area;
    }

    const drawDeliveryInfo = () => {
        const deliveryAvailable = isDeliveryAvailable();

        if (deliveryAvailable) {
            if (session.min_order_price_money?.amount > 0 && cart.total?.amount < session.min_order_price_money?.amount) {
                return <>
                    <span>{`${t("modules.cart.field.min_order_amount.title")}: `}</span>
                    <span className="text-primary"><Money value={session.min_order_price_money}/></span>
                </>
            }
            if (session.full_address) {
                return session.full_address;
            }
            return (
                <span className="text-primary">
                    {t("modules.cart.field.check_delivery_option.title")}
                </span>
            )
        } else {
            return <span className="text-danger">
                {t("modules.cart.field.delivery_unavailable.description")}
            </span>
        }
    }

    const drawFullDeliveryInfo = () => {
        const infoDetails = getInfoDetails();

        return (
            <>
                <div>
                    <span className="font-weight-bold">
                        {`${t("enums.order.type.DELIVERY")}: `}
                        {infoDetails.topInfo}
                    </span>
                </div>
                <div>
                    {infoDetails.bottomInfo}
                </div>
            </>
        )
    };
    return (
        <div className="cart-button-info" onClick={handleShowOrderDetailsModal} tabIndex={0} role="button"
             onKeyDown={handleOnKeyDown}
        >
            <RoundedIcon icon={<CarIco/>} size="medium"
                         className={isDeliveryAvailable() ? "text-primary" : "text-danger"}/>
            <div className="cart-button-info-middle-box">
                {drawFullDeliveryInfo()}
            </div>
            <DropdownIco className="ms-auto dropdown-ico"/>
        </div>
    )
}

const mapStateToProps = (state: RootState) => ({
    cart: getCart(state.cart),
    session: getSession(state.session),
    config: getStoreConfig(state.config),
    availabilities: getAvailabilities(state.menu),
    menus: getMenus(state.menu),
    locale: selectLocale(state.session),
})

const connector = connect(mapStateToProps);
export default withTranslation()(connector(CartButtonDeliveryInfo));